/**
 * ProvenanceExceptionTable — Exception-focused provenance data table.
 *
 * Shows failed, incomplete, and missing provenance actions with filters
 * by agent, system, policy, and time window.
 *
 * Per plan Phase 3: Exception table with filters for investigation.
 */

import type { ProvenanceStatus } from './provenance-types';
import { PROVENANCE_COPY, provenanceStatusSeverity } from './provenance-types';

const React = (globalThis as any).React;
const { useState, useEffect, useRef, useCallback } = React;
const { usePlugin } = (globalThis as any).PluginSDK || {};

export interface ProvenanceException {
  action_id: string;
  status: ProvenanceStatus;
  agent_name: string;
  system: string;
  tool_id: string;
  timestamp: string;
  failure_reason?: string;
  missing_bands?: string[];
}

export interface ProvenanceExceptionTableProps {
  onViewEvidence?: (actionId: string) => void;
}

type FilterStatus = 'all' | 'failed' | 'partial' | 'missing';

const STATUS_COLORS: Record<string, string> = {
  failed: 'var(--color-danger, #ef4444)',
  partial: 'var(--color-warning, #f59e0b)',
  missing: 'var(--main-text-secondary)',
  verified: 'var(--color-success, #22c55e)',
};

export function ProvenanceExceptionTable({ onViewEvidence }: ProvenanceExceptionTableProps) {
  const plugin = usePlugin?.() ?? null;
  const sdkApi = plugin?.sdk?.api ?? null;
  const [exceptions, setExceptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [agentFilter, setAgentFilter] = useState('');
  const mountedRef = useRef(true);

  const loadExceptions = useCallback(async () => {
    const resolveFetch = (): typeof globalThis.fetch => {
      if (sdkApi?.fetch) return sdkApi.fetch;
      return globalThis.fetch.bind(globalThis);
    };

    setLoading(true);
    setFetchError(null);

    try {
      const response = await resolveFetch()('/api/v1/analytics/provenance/exceptions', {
        method: 'GET',
        credentials: 'include' as RequestCredentials,
        headers: { 'Content-Type': 'application/json' },
      });
      if (!mountedRef.current) return;
      if (!response.ok) {
        setFetchError(`Failed to load exceptions (HTTP ${response.status})`);
        return;
      }
      const data = await response.json();
      const VALID_STATUSES: Set<string> = new Set(['failed', 'partial', 'missing', 'pending', 'verified']);
      const items: ProvenanceException[] = (data.exceptions ?? [])
        .filter((e: any) => e.action_id && VALID_STATUSES.has(e.status))
        .map((e: any) => ({
          action_id: e.action_id,
          status: e.status as ProvenanceStatus,
          agent_name: e.agent_name ?? '',
          system: e.system ?? '',
          tool_id: e.tool_id ?? '',
          timestamp: e.timestamp ?? '',
          failure_reason: e.failure_reason,
          missing_bands: e.missing_bands,
        }));
      setExceptions(items);
    } catch {
      if (mountedRef.current) {
        setFetchError('Unable to reach provenance service. Exception data is unavailable.');
      }
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  }, [sdkApi]);

  useEffect(() => {
    mountedRef.current = true;
    loadExceptions();
    return () => { mountedRef.current = false; };
  }, [loadExceptions]);

  const filtered = exceptions.filter((e: any) => {
    if (statusFilter !== 'all' && e.status !== statusFilter) return false;
    if (agentFilter && !e.agent_name.toLowerCase().includes(agentFilter.toLowerCase())) return false;
    return true;
  });

  const tableStyle: Record<string, string | number> = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: '13px',
  };

  const thStyle: Record<string, string | number> = {
    padding: '10px 12px',
    textAlign: 'left',
    fontWeight: 600,
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: 'var(--main-text-secondary)',
    borderBottom: '1px solid var(--card-border)',
  };

  const tdStyle: Record<string, string | number> = {
    padding: '10px 12px',
    borderBottom: '1px solid var(--card-border, rgba(255,255,255,0.04))',
    color: 'var(--main-text)',
  };

  if (loading) {
    return React.createElement('div', {
      'data-testid': 'exception-table-loading',
      style: { padding: '24px', textAlign: 'center', color: 'var(--main-text-secondary)' },
    }, 'Loading exceptions...');
  }

  if (fetchError) {
    return React.createElement('div', {
      'data-testid': 'exception-table-error',
      role: 'alert',
      style: {
        padding: '24px', textAlign: 'center',
        background: 'color-mix(in srgb, var(--color-danger, #ef4444) 8%, transparent)',
        borderRadius: '8px', border: '1px solid color-mix(in srgb, var(--color-danger, #ef4444) 20%, transparent)',
      },
    },
      React.createElement('div', {
        style: { fontSize: '14px', fontWeight: 600, color: 'var(--color-danger, #ef4444)', marginBottom: '8px' },
      }, '\u26A0 Exception data unavailable'),
      React.createElement('div', {
        style: { fontSize: '13px', color: 'var(--main-text-secondary)', marginBottom: '12px' },
      }, fetchError),
      React.createElement('button', {
        type: 'button',
        onClick: loadExceptions,
        style: {
          padding: '6px 16px', fontSize: '13px', fontWeight: 500,
          borderRadius: '6px', border: '1px solid var(--card-border)',
          background: 'transparent', cursor: 'pointer', color: 'var(--main-text)',
        },
      }, 'Retry'),
    );
  }

  return React.createElement('div', { 'data-testid': 'exception-table' },
    // Filters
    React.createElement('div', {
      style: { display: 'flex', gap: '12px', marginBottom: '16px', alignItems: 'center' },
    },
      React.createElement('select', {
        value: statusFilter,
        onChange: (e: any) => setStatusFilter(e.target.value),
        style: {
          padding: '6px 12px', borderRadius: '6px', fontSize: '13px',
          border: '1px solid var(--card-border)', background: 'var(--card-bg)',
          color: 'var(--main-text)',
        },
      },
        React.createElement('option', { value: 'all' }, 'All statuses'),
        React.createElement('option', { value: 'failed' }, 'Failed'),
        React.createElement('option', { value: 'partial' }, 'Incomplete'),
        React.createElement('option', { value: 'missing' }, 'Missing'),
      ),
      React.createElement('input', {
        type: 'text',
        placeholder: 'Filter by agent...',
        value: agentFilter,
        onChange: (e: any) => setAgentFilter(e.target.value),
        style: {
          padding: '6px 12px', borderRadius: '6px', fontSize: '13px',
          border: '1px solid var(--card-border)', background: 'var(--card-bg)',
          color: 'var(--main-text)', width: '200px',
        },
      }),
      React.createElement('span', {
        style: { fontSize: '12px', color: 'var(--main-text-secondary)' },
      }, `${filtered.length} exception${filtered.length !== 1 ? 's' : ''}`),
    ),

    // Table
    React.createElement('table', { style: tableStyle },
      React.createElement('thead', null,
        React.createElement('tr', null,
          React.createElement('th', { style: thStyle }, 'Status'),
          React.createElement('th', { style: thStyle }, 'Agent'),
          React.createElement('th', { style: thStyle }, 'System'),
          React.createElement('th', { style: thStyle }, 'Tool'),
          React.createElement('th', { style: thStyle }, 'Time'),
          React.createElement('th', { style: thStyle }, 'Action'),
        ),
      ),
      React.createElement('tbody', null,
        filtered.length === 0
          ? React.createElement('tr', null,
              React.createElement('td', { colSpan: 6, style: { ...tdStyle, textAlign: 'center', color: 'var(--main-text-secondary)' } },
                'No exceptions found',
              ),
            )
          : filtered.map((exc: ProvenanceException) =>
              React.createElement('tr', { key: exc.action_id },
                React.createElement('td', { style: tdStyle },
                  React.createElement('span', {
                    style: {
                      padding: '2px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: 600,
                      color: STATUS_COLORS[exc.status] ?? 'var(--main-text-secondary)',
                      background: `color-mix(in srgb, ${STATUS_COLORS[exc.status] ?? 'transparent'} 12%, transparent)`,
                    },
                  }, PROVENANCE_COPY[exc.status]?.inline ?? exc.status),
                ),
                React.createElement('td', { style: tdStyle }, exc.agent_name),
                React.createElement('td', { style: tdStyle }, exc.system),
                React.createElement('td', { style: { ...tdStyle, fontFamily: 'monospace', fontSize: '11px' } }, exc.tool_id),
                React.createElement('td', { style: { ...tdStyle, fontSize: '11px', color: 'var(--main-text-secondary)' } },
                  new Date(exc.timestamp).toLocaleString(),
                ),
                React.createElement('td', { style: tdStyle },
                  onViewEvidence && React.createElement('button', {
                    type: 'button',
                    onClick: () => onViewEvidence(exc.action_id),
                    style: {
                      border: 'none', background: 'transparent', cursor: 'pointer',
                      color: 'var(--main-accent, #818cf8)', fontSize: '12px', fontFamily: 'inherit',
                    },
                  }, 'View evidence'),
                ),
              ),
            ),
      ),
    ),
  );
}

export default ProvenanceExceptionTable;
